import z from "zod";
import { MoneyScheme } from "./money";
import {
  ModifierIdScheme,
  PaymentMethodIdScheme,
  ProductGroupIdScheme,
  ProductIdScheme,
  StoreIdScheme,
} from "./ids";
import { ReceiptType } from "./enums/receiptType";
import { PaymentMethodType } from "./enums/PaymentMethodType";

const ReceiptTypeTotalScheme = z.object({
  count: z.number(),
  grossAmount: MoneyScheme,
});

const PaymentMethodTotalScheme = z.object({
  count: z.number(),
  grossAmount: MoneyScheme,
  name: z.string().optional(),
  type: z.enum(PaymentMethodType).optional(),
});

const HourOfDayScheme = z.object({
  receiptsCount: z.array(z.number()).length(24),
  grossAmount: z.array(MoneyScheme).length(24),
  refundsCount: z.array(z.number()).length(24).optional(),
  refundsAmount: z.array(MoneyScheme).length(24).optional(),
});

export const StoreStatsDBScheme = z.object({
  tz: z.string(),
  period: z.union([z.literal("day"), z.literal("week"), z.literal("month")]),
  rangeStartUtc: z.string(),

  receipts: z.object({
    count: z.number(),
    grossAmount: MoneyScheme,
    netAmount: MoneyScheme,
    vat: MoneyScheme,
  }),

  receiptTypes: z.record(z.enum(ReceiptType), ReceiptTypeTotalScheme),

  discounts: z.object({
    amount: MoneyScheme,
    count: z.number(),
  }),

  paymentMethods: z.record(PaymentMethodIdScheme, PaymentMethodTotalScheme),

  hourOfDay: HourOfDayScheme,

  computedAt: z.iso.datetime(),
  version: z.number(),
});

export type StoreStatsDB = z.infer<typeof StoreStatsDBScheme>;

export const ChainStatsDBScheme = StoreStatsDBScheme.extend({
  storeCount: z.number(),
  storesWithStats: z.number(),
});

export type ChainStatsDB = z.infer<typeof ChainStatsDBScheme>;

export const ChainStatsStoreDBScheme = StoreStatsDBScheme.extend({
  storeId: StoreIdScheme,
  storeName: z.string(),
});

export type ChainStatsStoreDB = z.infer<typeof ChainStatsStoreDBScheme>;

const StoreStatsItemBaseScheme = z.object({
  quantity: z.number(),
  grossAmount: MoneyScheme,
  netAmount: MoneyScheme,
  discountAmount: MoneyScheme,
  // FIFO cost of goods, for stores with inventory costing
  costAmount: MoneyScheme.optional(),
  costProvisional: z.boolean().optional(),
  computedAt: z.iso.datetime(),
  version: z.number(),
  name: z.string().optional(),
});

export const StoreStatsProductDBScheme = StoreStatsItemBaseScheme.extend({
  productId: z.union([ProductIdScheme, ModifierIdScheme]),
});

export const StoreStatsGroupDBScheme = StoreStatsItemBaseScheme.extend({
  groupId: ProductGroupIdScheme,
});

export type StoreStatsProductDB = z.infer<typeof StoreStatsProductDBScheme>;
export type StoreStatsGroupDB = z.infer<typeof StoreStatsGroupDBScheme>;
